import { encodeParam } from "utilities/misc";
import { deleteRequest, get, post } from "utilities/network";

export interface UserSecurityStatus {
  securityRestrictedAt: string | null;
  googleAuthenticatorEnabled: boolean;
  googleAuthenticatorPending: boolean;
}
export const httpUserSecurity = (userID: number): Promise<UserSecurityStatus> =>
  get(`admin/users/${encodeParam(userID)}/security`);
export const httpRestoreUserAccess = (userID: number, note: string) =>
  post(`admin/users/${encodeParam(userID)}/security-restriction/restore`, {
    note,
  });
export const httpResetGoogleAuthenticator = (userID: number, note: string) =>
  post(`admin/users/${encodeParam(userID)}/google-authenticator/reset`, {
    note,
  });

export const httpFetchUsers = (
  limit: number,
  page: number,
  order: string = "A-Z",
  kycReviewStatus?: string,
  userID?: string,
) => {
  const params = new URLSearchParams({
    limit: String(limit),
    page: String(page),
    order,
  });
  if (kycReviewStatus) {
    params.append("kycReviewStatus", kycReviewStatus);
  }
  if (userID) params.append("userID", userID);
  return get(`admin/users?${params.toString()}`);
};

export const httpUserBalances = (userID: string) => {
  return get(`admin/balances/user/${encodeParam(userID)}`);
};

export const httpCreateUserLien = (
  userID: string | number,
  payload: {
    amount: string;
    currency: string;
    reason: string;
    reference?: string;
  },
) => post(`admin/users/${encodeParam(userID)}/liens`, payload);

export const httpReleaseUserLien = (
  userID: string | number,
  lienID: string | number,
) =>
  post(
    `admin/users/${encodeParam(userID)}/liens/${encodeParam(lienID)}/release`,
    {},
  );

export const httpUserWallet = (userID: string) => {
  return get(`admin/users/userWallet/${encodeParam(userID)}`);
};

export const httpUserRevenue = (
  id: string,
  type: string,
  date: Date | string,
) => {
  return get(
    `admin/users/revenue/${encodeParam(id)}?date=${encodeParam(date)}&type=${encodeParam(type)}`,
  );
};

export const httpUserTransactionAnalytics = (
  id: string,
  type: string,
  date: Date | string,
) => {
  return get(
    `admin/users/transactionAnalytics/${encodeParam(id)}?date=${encodeParam(date)}&type=${encodeParam(type)}`,
  );
};

export const httpAcquisitions = (type: string, date: Date | string) => {
  return get(
    `admin/analytics/acquisitions?date=${encodeParam(date)}&type=${encodeParam(type)}`,
  );
};

export const httpUserTransactions = (
  id: string,
  currencyType: string,
  transactionType: string,
  place: string,
  limit?: number,
  page?: number,
) => {
  return get(
    `admin/users/transactions/${encodeParam(id)}?transactionType=${encodeParam(transactionType)}&place=${encodeParam(place)}&currencyType=${encodeParam(currencyType)}&limit=${encodeParam(limit)}&page=${encodeParam(page)}`,
  );
};

export const httpUSearchUser = (userName: string, signal?: AbortSignal) => {
  return get(`admin/users/search?search=${encodeParam(userName)}`, signal);
};

export const httpUserKyc = (userID: string | number) => {
  return get(`admin/users/${userID}/kyc`);
};

export const httpRejectUserKyc = (userID: string | number, reason: string) => {
  return post(`admin/users/${userID}/kyc/reject`, { reason });
};

export const httpEscalateUserKyc = (
  userID: string | number,
  reason: string,
) => {
  return post(`admin/users/${userID}/kyc/escalate`, { reason });
};

export const httpUserPriceAlerts = (userID: string | number) => {
  return get(`admin/users/${userID}/price-alerts`);
};

export const httpClearUserPriceAlert = (
  userID: string | number,
  alertID: string | number,
  reason: string,
) => {
  return deleteRequest(`admin/users/${userID}/price-alerts/${alertID}`, {
    reason,
  });
};

export const httpUserBeneficiaries = (userID: string | number) => {
  return get(`admin/users/${userID}/beneficiaries`);
};

export const httpUserSavedAddresses = (userID: string | number) => {
  return get(`admin/users/${userID}/saved-addresses`);
};

export const httpUserAccountStatements = (userID: string | number) => {
  return get(`admin/users/${userID}/account-statements`);
};

export const httpUserInternalNotes = (userID: string | number) => {
  return get(`admin/users/${userID}/internal-notes`);
};

export const httpAppendUserInternalNote = (
  userID: string | number,
  note: string,
) => {
  return post(`admin/users/${userID}/internal-notes`, { note });
};

export const httpFetchAddressVerificationReviews = (
  status: string,
  limit: number = 20,
  page: number = 1,
) =>
  get(
    `admin/users/addressVerificationReviews?status=${encodeParam(status)}&limit=${encodeParam(limit)}&page=${encodeParam(page)}`,
  );

export const httpFetchKycReviews = (
  status: string,
  limit: number = 20,
  page: number = 1,
) =>
  get(
    `admin/users/kycReviews?status=${encodeParam(status)}&limit=${encodeParam(limit)}&page=${encodeParam(page)}`,
  );

export const httpGetAddressVerificationReview = (id: number) =>
  get(`admin/users/addressVerificationReviews/${encodeParam(id)}`);

export const httpGetKycReview = (id: number) =>
  get(`admin/users/kycReviews/${encodeParam(id)}`);

export const httpDecideIdentityVerification = (
  id: number,
  decision: "approve" | "reject",
  reason: string,
  verificationId: string,
  reviewRevision: string,
) =>
  post(`admin/users/kycReviews/${encodeParam(id)}/identity/${decision}`, {
    reason,
    verificationId,
    reviewRevision,
  });

export const httpDecideAddressVerification = (
  id: number,
  decision: "approve" | "reject",
  reason: string,
  verificationId: string,
  reviewRevision: string,
) =>
  post(
    `admin/users/addressVerificationReviews/${encodeParam(id)}/${decision}`,
    {
      reason,
      verificationId,
      reviewRevision,
    },
  );

export const httpGrowthMetrics = (month: string, type: string) => {
  return get(
    `admin/analytics/growth-metrics?month=${encodeParam(month)}&type=${encodeParam(type)}`,
  );
};

export const httpGenerateAdminMarket = (
  crypto: string,
  fromDate: string,
  toDate: string,
) => {
  return post(`admin/transactions/generateAdminMarket`, {
    crypto,
    fromDate,
    toDate,
  });
};

export const httpSaveAdminMarket = (
  crypto: string,
  fromDate: string,
  toDate: string,
) => {
  return post(`admin/transactions/saveAdminMarket`, {
    crypto,
    fromDate,
    toDate,
  });
};

export const httpFetchVerificationEmails = (id: number, page: number) =>
  get(
    `admin/users/kycReviews/${encodeParam(id)}/emails?page=${encodeParam(page)}`,
  );

export interface UserElo {
  standing: {
    totalPoints: number;
    tier: { name: string } | null;
    nextTier: { name: string; lowerLimit: number } | null;
    risk: {
      state: string;
      deadlineAt: string | null;
      pointsToMaintainTier: number;
    };
  };
  recentAwards: {
    id: number;
    points: number;
    eloCategory: string;
    sourceType: string;
    sourceId: string;
  }[];
}
export const httpUserElo = (userID: number): Promise<UserElo> =>
  get(`admin/gamification/users/${userID}/elo`);
